import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchVapidPublicKey,
  subscribePush,
  unsubscribePush,
  urlBase64ToUint8Array,
} from './pushClient';
import {
  isIOS,
  isStandalone,
  pushSupported,
  resolvePushAvailability,
  type PushAvailability,
} from './pushEnvironment';

/**
 * Per-DEVICE web-push subscription state and control (PLAN slice 13b, item 2).
 *
 * "Per-device, not per-user" is honoured by reading the browser's OWN
 * `pushManager.getSubscription()` for this device's state, rather than a
 * tenant-wide list — the subscription lives in THIS browser, so this browser is
 * the truth for whether it is on. Enable/disable push the change through raad-api
 * (subscribe/unsubscribe); the API never reaches Traccar.
 *
 * The VAPID public key is fetched at runtime (seam S1). Until it resolves the
 * control is `loading` (rendered as absent, no flash); if it resolves to `null`
 * the feature is UNCONFIGURED and the control is absent for good (AC-12).
 */

export type PushError = 'permission-denied' | 'failed' | null;

export interface PushNotifications {
  /** `loading` until the VAPID key query settles; then the resolved platform state. */
  availability: PushAvailability | 'loading';
  /** This device currently has an active push subscription. */
  subscribed: boolean;
  /** The browser Notification permission — `denied` means the user must unblock it
   *  in browser settings; we cannot re-prompt. */
  permission: NotificationPermission;
  /** An enable/disable call is in flight. */
  busy: boolean;
  error: PushError;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

const VAPID_QUERY_KEY = ['push', 'vapid-public-key'] as const;

async function readRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export function usePushNotifications(): PushNotifications {
  // Platform facts, read once on mount — a session does not switch OS mid-flight,
  // and re-reading `standalone` on every render is pointless churn.
  const [env] = useState(() => ({
    ios: isIOS(),
    standalone: isStandalone(),
    supported: pushSupported(),
  }));

  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PushError>(null);
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );

  const vapidQuery = useQuery({
    queryKey: VAPID_QUERY_KEY,
    queryFn: ({ signal }) => fetchVapidPublicKey(signal),
    staleTime: Infinity,
    retry: false,
  });

  const configured = vapidQuery.data != null;

  const availability: PushAvailability | 'loading' = vapidQuery.isPending
    ? 'loading'
    : resolvePushAvailability({
        configured,
        ios: env.ios,
        standalone: env.standalone,
        supported: env.supported,
      });

  // Read this device's existing subscription once we know push can work here.
  useEffect(() => {
    let cancelled = false;
    if (availability !== 'ready') return;
    void (async () => {
      const reg = await readRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (!cancelled) setSubscribed(Boolean(sub));
    })();
    return () => {
      cancelled = true;
    };
  }, [availability]);

  const enable = useCallback(async () => {
    const publicKey = vapidQuery.data;
    if (!publicKey) return; // unconfigured — nothing to subscribe against
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError('permission-denied');
        return;
      }
      const reg = await readRegistration();
      if (!reg) {
        setError('failed');
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = sub.toJSON();
      const endpoint = json.endpoint;
      const p256dh = json.keys?.p256dh;
      const auth = json.keys?.auth;
      if (!endpoint || !p256dh || !auth) {
        setError('failed');
        return;
      }
      await subscribePush({ endpoint, keys: { p256dh, auth } });
      setSubscribed(true);
    } catch {
      setError('failed');
    } finally {
      setBusy(false);
    }
  }, [vapidQuery.data]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await readRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        // Tell the server too, so it stops sending to a dead endpoint. The record
        // of anything already dispatched survives (endpoint frozen on the dispatch).
        await unsubscribePush(endpoint);
      }
      setSubscribed(false);
    } catch {
      setError('failed');
    } finally {
      setBusy(false);
    }
  }, []);

  return { availability, subscribed, permission, busy, error, enable, disable };
}
